import { CommonModule } from '@angular/common';
import { Component, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { AuthService } from '../../services/auth.service';
import { CaptchaComponent } from '../captcha/captcha.component';
import { ErrorMessageComponent } from '../error-message/error-message.component';

/**
 * LoginFormComponent provides a reusable login form UI that handles email/password authentication, "remember me" state, optional CAPTCHA verification, validation, and detailed error handling with user-facing messages.
 * @component
 * @selector app-login-form
 * @example
 *   <app-login-form></app-login-form>
 * @input None - This component does not expose any @Input properties; it manages its own FormGroup internally.
 * @output None - This component does not emit @Output events; navigation and user notifications are handled via injected services.
 *
 * Detailed description:
 * - Purpose: Collects user credentials (email, password, rememberMe), performs client-side validation, optionally requires a CAPTCHA after repeated failures, and delegates authentication to an AuthService. It surfaces errors via inline form error strings and a NotificationService for toast-style notifications.
 * - Key properties:
 *   - loginForm: FormGroup containing email, password, and rememberMe controls with required/format/minLength validators.
 *   - isSubmitting: boolean flag indicating an in-flight submission.
 *   - showPassword: boolean flag used to toggle password visibility in the template.
 *   - showCaptcha: boolean that toggles display of the CaptchaComponent after repeated failed attempts.
 *   - loginErrorCount: number tracking consecutive login failures to trigger CAPTCHA.
 *   - emailError / passwordError: strings used to display inline validation or server error messages.
 *   - captchaComponent (ViewChild): reference to the nested CaptchaComponent used for generation and verification.
 * - Key methods:
 *   - togglePasswordVisibility(): Toggles the showPassword flag so the template can reveal or hide the password input.
 *   - login(): Primary handler that:
 *       1) Verifies CAPTCHA when shown.
 *       2) Validates the form and sets isSubmitting.
 *       3) Calls AuthService.login(...) and reacts to success or failure.
 *       4) Handles specific server responses (429 Too Many Attempts, 403 account not active, 401 account inactive) by showing appropriate NotificationService notifications.
 *       5) Maps various error messages to user-facing inline errors, increments loginErrorCount, and enables/regenerates CAPTCHA after repeated failures.
 * - Services / dependencies:
 *   - Router: for potential navigation after successful authentication (injected).
 *   - AuthService: performs the authentication HTTP request.
 *   - NotificationService: displays toast/notification messages for error states.
 * - Lifecycle hooks:
 *   - No Angular lifecycle hooks (e.g., ngOnInit) are implemented in this component; initialization is handled by property initializers.
 */
@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    CaptchaComponent,
    ErrorMessageComponent,
  ],
  templateUrl: './login-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent {
  @ViewChild(CaptchaComponent) captchaComponent!: CaptchaComponent;
  isSubmitting = false;
  emailError = '';
  passwordError = '';

  loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(2)]),
    rememberMe: new FormControl(false),
  });

  showPassword = false;
  showCaptcha = false;
  loginErrorCount = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private messageService: NotificationService,
    // Angular 22 runs this app zoneless — without an explicit markForCheck the
    // submit button stays on "Loading..." after the request settles and the
    // inline credential error never appears.
    private cdr: ChangeDetectorRef
  ) {}

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  /**
   * Attempts to log in the user: validates captcha and form, submits credentials, and handles server responses and UI state.
   * @example
   * ogin()
   * // Triggers authentication request and updates component state (no direct return value)
   * @param {void} none - No parameters.
   * @returns {void} No return value; performs side effects (HTTP request, message display, form error handling, captcha generation).
   */
  login() {
    if (this.showCaptcha) {
      const isValid = this.captchaComponent.verifyCaptcha(
        this.captchaComponent.captchaInput.nativeElement.value
      );
      if (!isValid) {
        return;
      }
    }

    if (this.loginForm.invalid) {
      return;
    }
    this.isSubmitting = true;
    this.emailError = '';
    this.passwordError = '';

    this.authService.login(this.loginForm.value as any).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.cdr.markForCheck();
      },
      error: err => {
        this.isSubmitting = false;
        // Marked here rather than per-branch: the handler below has several early
        // returns, and one markForCheck dirties the view for everything this
        // callback sets synchronously.
        this.cdr.markForCheck();

        // Check for status_code in error response
        const statusCode = err?.status_code || err?.error?.status_code;
        const errorMessage = err?.message || err?.error?.message || '';

        // Handle 429 - Too many login attempts
        if (statusCode === 429 && errorMessage.includes('Too many login attempts')) {
          this.messageService.add({
            severity: 'error',
            summary: 'Too Many Attempts',
            detail: 'Too many login attempts. Please try again after 2 minutes.',
            life: 5000,
          });
          return;
        }

        // Handle 403 - Account not active/deleted, or (CWS-115) not active *yet*: an employee
        // whose start date has not arrived. Keep the server's wording there so the toast names
        // the date they can sign in, instead of reading as a permanent lockout.
        if (statusCode === 403) {
          const notStartedYet = errorMessage.includes('not active yet');
          this.messageService.add({
            severity: 'error',
            summary: notStartedYet ? 'Account Not Active Yet' : 'Account Not Active',
            detail: notStartedYet ? errorMessage : 'Your account is not active.',
            life: 5000,
          });
          return;
        }
        if (statusCode === 401 && errorMessage.includes('Your account is not active.')) {
          this.messageService.add({
            severity: 'error',
            summary: 'Account Not Active',
            detail: 'Your account is not active.',
            life: 5000,
          });
          return;
        }

        // Handle other errors
        if (err && errorMessage) {
          if (errorMessage.includes('Incorrect password')) {
            this.passwordError = 'Invalid credentials';
          } else if (errorMessage.includes('Invalid Email')) {
            this.passwordError = 'Invalid credentials';
          } else {
            this.passwordError = 'Invalid credentials';
          }

          this.loginErrorCount++;
          if (this.loginErrorCount >= 3) {
            if (this.showCaptcha) {
              this.captchaComponent.generateCaptcha();
            }
            this.showCaptcha = true;
          }
        } else {
          this.passwordError = 'Invalid credentials';
        }
        console.error(err);
      },
    });
  }
}
